'use client';

import { useEffect, useMemo, type CSSProperties } from 'react';
import { getAppUpdateCopy } from '@/i18n/app-update-copy';
import { getStaticUiCopy } from '@/i18n/static-ui-copy';
import { getPostHogSupportCopy } from '@/i18n/posthog-support-copy';
import { getDocumentDirection } from '@/i18n/config';
import { isDeploySkewCrash } from '@/lib/deploy-skew';
import { autoReloadHiddenTabOnce } from '@/lib/deploy-skew-reload';
import { useChunkLoadRecovery } from '@/hooks/use-chunk-load-recovery';
import { captureNextGlobalError } from '../../lib/observability/posthog-global-error-capture';
import { reportPageCrash } from '@/lib/observability/user-blocking-failure-reporter';

const BUTTON_STYLE = {
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid #ddd',
  background: '#fff',
  cursor: 'pointer',
  minHeight: 44,
  display: 'inline-flex',
  alignItems: 'center',
} satisfies CSSProperties;

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const locale =
    typeof document !== 'undefined'
      ? document.documentElement.lang || 'en'
      : 'en';
  const ui = getStaticUiCopy(locale);
  const supportCopy = getPostHogSupportCopy(locale);
  const updateCopy = getAppUpdateCopy(locale);
  const deploySkew = useMemo(() => isDeploySkewCrash(error), [error]);
  useChunkLoadRecovery(deploySkew ? error : undefined);

  useEffect(() => {
    if (deploySkew) autoReloadHiddenTabOnce();
  }, [deploySkew]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.error(error);
    }
    reportPageCrash(error, { deploySkew });
    captureNextGlobalError(
      error,
      typeof window === 'undefined' ? null : window.location.pathname
    );
  }, [deploySkew, error]);

  return (
    <html lang={locale} dir={getDocumentDirection(locale)}>
      <body>
        {deploySkew ? (
          // The tab is out of date with a new deploy; a reload fixes it.
          <div
            data-app-update-screen="true"
            style={{ padding: 24, fontFamily: 'system-ui, sans-serif' }}
          >
            <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
              {updateCopy.title}
            </h1>
            <p style={{ marginBottom: 16, color: '#666' }}>
              {updateCopy.description}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={BUTTON_STYLE}
            >
              {updateCopy.reload}
            </button>
          </div>
        ) : (
          <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif' }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
              {ui.somethingWentWrong}
            </h2>
            <p style={{ marginBottom: 16, color: '#666' }}>
              {ui.pleaseTryAgainContactSupport}
            </p>
            <button type="button" onClick={() => reset()} style={BUTTON_STYLE}>
              {ui.tryAgain}
            </button>
            <a
              href="mailto:contact@imai.studio"
              style={{
                marginInlineStart: 12,
                minHeight: 44,
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              {supportCopy.contactEmail}
            </a>
          </div>
        )}
      </body>
    </html>
  );
}
